import { useState } from "react";
import { Send, Check } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n";
import { track } from "@/utils/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactForm({ compact = false }) {
  const { lang, t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    if (!form.name || !form.email || !form.message) {
      toast(t("contact_required"));
      return;
    }
    setSending(true);
    try {
      const r = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...form, language: lang }),
      });
      if (!r.ok) throw new Error();
      setSent(true);
      track("contact_submit");
    } catch {
      toast.error(t("contact_error"));
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="border border-sage/60 p-8 text-center" data-testid="contact-sent">
        <div className="w-12 h-12 rounded-full bg-sage/20 flex items-center justify-center mx-auto mb-4">
          <Check size={22} strokeWidth={1.5} className="text-sage" />
        </div>
        <p className="font-serif text-2xl text-ink">{t("contact_sent")}</p>
        <p className="mt-2 text-sm text-ink/60">{t("contact_sent_sub")}</p>
      </div>
    );
  }

  return (
    <div className={compact ? "" : "border border-stone2/60 bg-ivory p-8"} data-testid="contact-form">
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <input
            placeholder={t("name_label")}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="contact-name"
            className="border border-stone2 bg-ivory px-5 py-3.5 text-sm text-ink focus:outline-none focus:border-medblue"
          />
          <input
            type="email"
            placeholder={t("email_label")}
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="contact-email"
            className="border border-stone2 bg-ivory px-5 py-3.5 text-sm text-ink focus:outline-none focus:border-medblue"
          />
        </div>
        <textarea
          rows={compact ? 4 : 6}
          placeholder={t("message_label")}
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          data-testid="contact-message"
          className="w-full border border-stone2 bg-ivory px-5 py-3.5 text-sm text-ink focus:outline-none focus:border-medblue resize-none"
        />
        <button
          onClick={submit}
          disabled={sending}
          data-testid="contact-submit"
          className="inline-flex items-center gap-2 bg-medblue text-ivory text-xs font-medium uppercase tracking-[0.2em] px-8 py-4 hover:bg-ink disabled:opacity-40 transition-colors duration-300"
        >
          <Send size={14} strokeWidth={1.5} /> {sending ? "..." : t("send_message")}
        </button>
        <p className="text-[11px] text-ink/40">{t("contact_privacy_note")}</p>
      </div>
    </div>
  );
}
